import * as React from "react";
import { useMemo, useState } from "react";

import { isEqual } from "lodash";
import queryString from "query-string";
import ReactDOMServer from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";

import categoryService, { CATEGORY_PREFIX } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { swSettings } from "app/common/services/swSettings";
import type { ICategoryItem } from "app/components/React/CategoriesDropdown/CategoryDropdown";
import { CategoryBenchmarkDropdown } from "app/components/React/CategoryBenchmarkDropdown/CategoryBenchmarkDropdown";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { WeShare } from "app/components/weshare/weshare";
import { useLoading } from "app/custom-hooks/loadingHook";
import { translate } from "app/filters/i18nFilter";
import { HDBanner } from "app/marketing/components/common/banners/HDBanner/HDBanner";
import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import {
    areValuesEqualAvgWithTreshold,
    BetaMessage,
    BetaMessagewrapper,
    DisclaimerMessage,
} from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/CommonComponents";
import { AddToDashboard } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/AddToDashboard";
import { GraphTypeSwitcher } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/GraphTypeSwitcher";
import { LegendsContainer } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/LegendsContainer";
import { TimeGranularitySwitcher } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/TimeGranularitySwitcher";
import {
    ButtonsContainer,
    ChartSection,
    CheckboxesContainer,
    UtilitiesContainer,
    ChartContainer,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import { getGA4FirstDataPointDate } from "app/pages/website-analysis/TrafficAndEngagement/Components/trafficCompareModePopContainer/GenericGraphHelper";
import { getChartConfig } from "app/pages/website-analysis/TrafficAndEngagement/MD/chartConfig";
import { tabs } from "app/pages/website-analysis/TrafficAndEngagement/MD/Tabs";
import { hasIsolatedDailyPoints } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/hasIsolatedDailyPoints";
import {
    addBetaBranchParam,
    addCategoryParam,
    ENDPOINT_PREFIX_WITH_CATEGORY_SUPPORT,
    getDualCalculations,
    pngDownload,
    webSources,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { PngHeader } from "app/UtilitiesAndConstants/UtilitiesComponents/PngHeader";
import Chart from "pro-features/components/Chart/src/Chart";
import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getTabTypeForTracking, noCategoryItem, unsupportedCategoryTabs } from "./TrafficAndEngagementTabs";

const CATEGORY_DROPDOWN_TOOLTIP_KEY = "traffic_and_engagement.generic_graph.category.dropdown.tooltip";
const CATEGORY_DROPDOWN_DISABLED_TOOLTIP_KEY =
    "traffic_and_engagement.generic_graph.category.dropdown.disabled.tooltip";

const isStacking = (webSource, chartType, isCategoryActive: boolean): boolean => {
    if (isCategoryActive || (chartType === "column" && webSource !== "Total")) {
        return false;
    }
    return ["column", "area"].includes(chartType);
};

const GenericGraphInner: React.FunctionComponent<any> = (props) => {
    const fetchService = DefaultFetchService.getInstance();
    const {
        metric,
        showGAApprovedData,
        showBetaBranchData,
        gaPrivacyStatus,
        params,
        chosenSites,
        hasGaToken,
        getSiteColor,
        meta,
        toggleMTD,
        updateExcelLink,
        chartIdForAnnotations,
        chartKey,
        selectedWidgetTab,
        setTotalCount,
    } = props;
    const { webSource, country, key, isWWW, duration, comparedDuration, category } = params;

    const typeForTracking = getTabTypeForTracking(chartKey);

    const [selectedCategory, setSelectedCategory] = useState<ICategory | ICategoryItem>(
        category && category !== noCategoryItem.id ? categoryService.getCategory(category) : noCategoryItem,
    );

    React.useEffect(() => {
        setSelectedCategory(
            category && category !== noCategoryItem.id ? categoryService.getCategory(category) : noCategoryItem,
        );
    }, [category]);

    const isCategoryEnabled = !meta.isPOP && !unsupportedCategoryTabs.includes(selectedWidgetTab);
    const isCategorySelected = selectedCategory?.id !== noCategoryItem.id;

    const onCategorySelected = (category: ICategory) => {
        allTrackers.dispatchTrackEvent("Dropdown", "Select industry", "Benchmark to industry", {
            selectedIndustry: category.text,
        });
        setSelectedCategory(category);
    };

    const onCategoryDropdownToggle = (isOpen: boolean) => {
        isOpen && allTrackers.dispatchTrackEvent("Dropdown", "open", "Benchmark to industry");
    };

    const durations = React.useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );
    const { from, to, compareFrom, compareTo, isWindow } = durations.forAPI;
    const lastSupportedDate = meta.isMTDActive ? swSettings.current.lastSupportedDailyDate : to.replace(/\|/g, "/");

    const isGa = showGAApprovedData && hasGaToken;
    const isGaPublic = gaPrivacyStatus === "Public";
    const [timeGranularityOptions, timeGranularityOptionsEnabled] = React.useMemo(() => {
        const opts = [
            {
                title: "D",
                disabled: metric.isTimeGranularityDisabled({ isGa, duration, title: "D" }, meta),
                name: "Daily",
                supportMTD: true,
            },
            {
                title: "W",
                disabled: metric.isTimeGranularityDisabled({ isGa, duration, title: "W" }, meta),
                name: "Weekly",
                supportMTD: true,
            },
            {
                title: "M",
                disabled: metric.isTimeGranularityDisabled({ isGa, duration, title: "M" }, meta),
                name: "Monthly",
                supportMTD: true,
            },
        ];
        const enabledOpts = opts.filter((opt) => !opt.disabled);
        return [opts, enabledOpts];
    }, [metric.isTimeGranularityDisabled, isGa, duration, showBetaBranchData, meta?.isFromAlternativeSources]);
    const getTimeGranularity = React.useCallback(() => timeGranularityOptions, [timeGranularityOptions]);

    const [graph, graphOps] = useLoading();
    const [chartType, setChartType] = React.useState(metric.getChartType(meta));
    const [timeGranularity, setTimeGranularity] = React.useState(
        timeGranularityOptionsEnabled[timeGranularityOptionsEnabled.length - 1],
    );
    const [showChart, setShowChart] = React.useState(false);
    const chartPngRef = React.useRef<HTMLDivElement>(null);
    const currentTab = tabs.find((tab) => tab.name === selectedWidgetTab) ?? tabs[0];

    const shouldUseCombinedWebSource =
        webSource === "Total" &&
        !currentTab.disableCombined &&
        meta.isSingleMode &&
        (!isCategoryEnabled || !isCategorySelected);
    const webSourceForApi = metric.getWebSourceForApi
        ? metric.getWebSourceForApi({ webSource, chartType })
        : shouldUseCombinedWebSource
          ? "Combined"
          : webSource;
    const lastQueryParams = React.useRef({});
    const queryParams = React.useMemo(() => {
        const newQueryParams = {
            ShouldGetVerifiedData: showGAApprovedData,
            compareTo,
            compareFrom,
            country,
            from,
            includeSubDomains: isWWW === "*",
            isWindow,
            keys:
                meta.isCategoryActive && isCategorySelected
                    ? addCategoryParam(key, (selectedCategory as ICategory).id)
                    : key,
            timeGranularity: timeGranularity.name,
            webSource: webSourceForApi,
            to,
            unBounced: meta.isUnBouncedActive,
            ...(comparedDuration !== "" && meta.isMTDActive ? { latestCompare: "l" } : null),
            ...(meta.isMTDActive ? { latest: "l" } : null),
        };
        // deeply compare to latest query params
        if (!isEqual(lastQueryParams.current, newQueryParams)) {
            lastQueryParams.current = newQueryParams;
        }
        return lastQueryParams.current;
    }, [
        metric.getWebSourceForApi,
        webSource,
        chartType,
        meta.isSingleMode,
        showGAApprovedData,
        compareTo,
        compareFrom,
        country,
        from,
        to,
        isWWW,
        isWindow,
        key,
        timeGranularity,
        meta.isMTDActive,
        meta.isUnBouncedActive,
        isCategoryEnabled,
        isCategorySelected,
    ]);

    const [series, setSeries] = React.useState(() => {
        if (meta.isSingleCompare) {
            return getDualCalculations();
        }
        let currentWebSource, currentCategory;
        const isWebSourceForApiCombined = webSourceForApi === "Combined";
        const compareSeries = chosenSites.map((site) => {
            return {
                name: site,
                color: getSiteColor(site),
                isSelected: true,
            };
        });
        currentWebSource = webSources.filter((ws) =>
            (isWebSourceForApiCombined ? ["Desktop", "MobileWeb"] : [webSourceForApi]).includes(ws.alias),
        );
        if (meta.isCategoryActive) {
            currentWebSource = currentWebSource.map((ws) => ({
                ...ws,
                name: compareSeries?.[0]?.name,
            }));

            currentCategory = {
                name: selectedCategory?.text,
                color: colorsPalettes.carbon[200],
                isSelected: true,
                alias: CATEGORY_PREFIX + (selectedCategory as ICategory).id,
                isCategory: true,
                websource: webSourceForApi,
            };
        }

        return metric.getSites
            ? metric.getSites({
                  isSingleMode: meta.isSingleMode,
                  compareSeries,
                  webSource,
                  durations: durations.forWidget,
                  compareSites: compareSeries,
              })
            : meta.isSingleMode
              ? [...currentWebSource, currentCategory].filter(Boolean)
              : [...compareSeries, currentCategory].filter(Boolean);
    });

    const metricTitle = React.useMemo(() => {
        const currentTab = tabs.find((tab) => tab.name === selectedWidgetTab) ?? tabs[0];
        const metricTitle = translate(currentTab.getTitle ? currentTab.getTitle(meta.is28Days) : currentTab.title);
        metric["title"] = metricTitle;
        return metricTitle;
    }, []);

    const isGraphLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(graph.state);

    const graphData = React.useMemo(() => {
        try {
            if (!graph.data) {
                return [];
            }
            const chosenSite = chosenSites[0];
            const params = {
                isSingleMode: meta.isSingleMode,
                sites: series,
                chosenSite,
                metric,
                webSource,
                chartType,
                timeGranularity,
                lastSupportedDate,
            };
            return metric.getGraphData({ ...params, data: graph.data }).map((site) => ({
                ...site,
                data: site?.data.map((dt) => ({ ...dt, y: dt.y === null ? null : Math.round(dt.y * 10000) / 10000 })),
            }));
        } catch (e) {
            return [];
        }
    }, [
        graph.data,
        meta.isSingleMode,
        series,
        chosenSites,
        metric,
        webSource,
        chartType,
        timeGranularity,
        lastSupportedDate,
    ]);

    const GA4FirstDataPointDate = getGA4FirstDataPointDate(graph, timeGranularity);

    const chartConfig = React.useMemo(() => {
        const lessEqualThanOneMonthDurations = ["28d", "1m"];
        const isTurboThreshold = timeGranularity.name === "Daily";
        const isDailyGranularity =
            timeGranularity.name !== "Monthly" && lessEqualThanOneMonthDurations.includes(duration);
        const isChartStacking = metric.isStacking
            ? metric.isStacking()
            : isStacking(webSource, chartType, meta.isCategoryActive)
              ? "normal"
              : null;
        const getChartXPointsLength = () =>
            graphData?.reduce((acc, site) => {
                site.data?.forEach(({ x }) => acc.add(x));
                return acc;
            }, new Set()).size;
        const isMarkerEnabled =
            (chartType !== "area" && timeGranularity.name !== "Daily") ||
            (lessEqualThanOneMonthDurations.includes(duration) && timeGranularity.name === "Monthly") ||
            getChartXPointsLength() <= 1 ||
            (timeGranularity.name === "Daily" && hasIsolatedDailyPoints(graphData));
        const getTooltipFormatter = function (): string {
            const ChartTooltip = metric.chartTooltip({
                points: this.points,
                x: this.x,
                graphData,
                timeGranularity: timeGranularity.name,
                lastSupportedDate,
                meta,
                GA4FirstDataPointDate,
            });
            return ReactDOMServer.renderToString(
                <>
                    {ChartTooltip}
                    {metric.getHdBannerCtaState ? (
                        <HDBanner
                            ctaState={metric.getHdBannerCtaState()}
                            dataAutomation="traffic-and-engagement-hd-banner-cta"
                        />
                    ) : null}
                </>,
            );
        };
        const { isFromAlternativeSources } = meta;
        return getChartConfig({
            metric,
            tooltipFormatter: getTooltipFormatter,
            isChartStacking,
            isMarkerEnabled,
            isDailyGranularity,
            isTurboThreshold,
            graphData,
            isFromAlternativeSources,
            GA4FirstDataPointDate,
        });
    }, [metric, timeGranularity, duration, webSource, graphData, chartType, GA4FirstDataPointDate]);

    const chartData = React.useMemo(() => {
        return graphData.filter((s) => s.isSelected);
    }, [graphData]);

    const isColumnChartTypeDisabled = React.useMemo(() => {
        return timeGranularity.name !== "Monthly" && ["25m"].includes(duration);
    }, [timeGranularity, duration]);

    const isSameData = React.useMemo(() => {
        if (meta.isSingleCompare && graphData?.length) {
            const graphKeys = (graphData[0].data ?? []).map((p) => p.x);
            return graphKeys.every((key, i) => {
                const values = [];
                const sameKeys = graphData.every((gr) => {
                    // check if same key - no more, no less
                    if (gr.data.length === graphKeys.length && i < gr.data.length && gr.data[i].x === key) {
                        values.push(gr.data[i]?.y);
                        return true;
                    }
                    return false;
                });
                return sameKeys && areValuesEqualAvgWithTreshold(values);
            });
        }
    }, [graphData, meta.isSingleCompare]);

    const onLegendClick = React.useCallback(
        (seriesItem) => {
            const newSeries = series.map((s) => {
                if (s.name === seriesItem.name) {
                    s.isSelected = !s.isSelected;
                }
                return s;
            });
            allTrackers.trackEvent(
                "Click Filter",
                seriesItem.isSelected ? "click" : "remove",
                `Over Time Graph/${typeForTracking}/${seriesItem.name}`,
            );
            setSeries(newSeries);
        },
        [series],
    );

    const granularityUpdate = React.useCallback(
        (granularityIndex) => {
            const newTimeGranularity = timeGranularityOptions[granularityIndex];
            allTrackers.trackEvent(
                "Time Frame Button",
                "switch",
                `Over Time Graph/${typeForTracking}/${newTimeGranularity.name}`,
            );
            if (!newTimeGranularity.supportMTD && meta.isMTDActive) {
                toggleMTD();
                setTimeout(() => setTimeGranularity(newTimeGranularity), 0);
            } else {
                setTimeGranularity(newTimeGranularity);
            }
        },
        [timeGranularityOptions, timeGranularity, toggleMTD],
    );

    const chartGranularityItemClick = React.useCallback(
        (index, shouldTrack = true) => {
            const newChartType = metric.chartGranularityItemClick
                ? metric.chartGranularityItemClick(index)
                : index === 1
                  ? "line"
                  : "column";
            setChartType(newChartType);
            if (shouldTrack) {
                allTrackers.trackEvent("Capsule Button", "switch", `${newChartType}/${typeForTracking}`);
            }
        },
        [metric.chartGranularityItemClick],
    );

    const chartAfterRender = React.useCallback((chart) => {
        // hasData() is provided by highcharts/modules/no-data-to-display
        // Use defensive check in case module isn't initialized yet
        const hasData =
            typeof chart.hasData === "function" ? chart.hasData() : chart.series?.some((s) => s.data?.length > 0);
        setShowChart(hasData);
        return chart;
    }, []);

    // NOTE: not in use, since each granularity supports MTD
    // // used when there is granularity that do not support MTD and MTD was toggled on for it,
    // // so this effect will choose the biggest granularity that supports MTD.
    // React.useEffect(() => {
    //     if (
    //         !timeGranularity ||
    //         (!timeGranularity.supportMTD && meta.isMTDActive) ||
    //         !timeGranularityOptionsEnabled.find((tg) => tg.name === timeGranularity.name)
    //     ) {
    //         const supportedTimeGranularity = timeGranularityOptionsEnabled.filter((tg) => !meta.isMTDActive || tg.supportMTD);
    //         setTimeGranularity(supportedTimeGranularity[supportedTimeGranularity.length - 1]);
    //     }
    // }, [timeGranularity, timeGranularityOptionsEnabled, meta.isMTDActive]);

    // *** data fetching for each tab in the graph
    React.useEffect(() => {
        graphOps.load(() => {
            let endpoint;
            if (metric.endpoint) {
                endpoint = metric.endpoint;
            } else {
                const endpointSuffix = `Graph${meta.isPOP ? "POP" : ""}`;
                const endpointMetric = !meta.isSingleMode && meta.isPOP ? "Overview" : metric.endpointMetric;
                endpoint = `${ENDPOINT_PREFIX_WITH_CATEGORY_SUPPORT}/${endpointMetric}/${endpointSuffix}`;
            }

            const apiParams = endpoint + "?" + queryString.stringify(queryParams);
            const apiParamsNew =
                endpoint + queryString.stringify(addBetaBranchParam(queryParams, true, meta.isSingleCompare));
            if (meta.isSingleCompare) {
                // for dual calculations aggregate graphs data by calculation
                return Promise.all<any>([fetchService.get(apiParamsNew), fetchService.get(apiParams)]).then(
                    (calcGraphs) => {
                        return calcGraphs.reduce(
                            (acc, calcGraph, idx) => ({
                                ...acc,
                                Data: {
                                    ...acc.Data,
                                    [series[idx].alias]: calcGraph.Data[chosenSites[0]],
                                },
                            }),
                            { ...calcGraphs?.[1], Data: {} },
                        );
                    },
                );
            } else if (showBetaBranchData) {
                return fetchService.get(apiParamsNew);
            }
            return fetchService.get(apiParams);
        });
    }, [metric.endpointMetric, queryParams, showBetaBranchData, meta.isSingleCompare]);

    React.useEffect(() => {
        if (graph.data && meta.isSingleCompare) {
            const chosenSite = chosenSites[0];
            if (graph.data.KeysDataVerification?.[chosenSite]) {
                setSeries(getDualCalculations(true));
            }
        }
    }, [graph.data, meta.isSingleCompare]);

    React.useEffect(() => {
        updateExcelLink(
            metric.getExcelLink
                ? metric.getExcelLink(addBetaBranchParam(queryParams, showBetaBranchData, meta.isSingleCompare))
                : null,
        );
    }, [metric.getExcelLink, queryParams, showBetaBranchData, meta.isSingleCompare]);
    const hasData = useMemo(() => {
        setTotalCount(chartData[0]?.data.length);
        if (chartData.length < 1) {
            return false;
        } else {
            return chartData.some((el) => {
                return el.data.some((dataPoint) => {
                    return dataPoint.y !== 0;
                });
            });
            // return false;
        }
    }, [chartData]);

    const displayCategoryBenchmarkDropdown =
        !meta.isPOP && !proNavigator.isSalesSolution() && !metric.hideCategoryBenchmarkDropdown;
    const categoryBenchmarkDropdownTooltipKey = isCategoryEnabled
        ? CATEGORY_DROPDOWN_TOOLTIP_KEY
        : CATEGORY_DROPDOWN_DISABLED_TOOLTIP_KEY;
    const { disclaimer } = metric;

    const downloadPngTrackingEvent = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", `Over Time Graph/${typeForTracking}/PNG`);
    };

    return (
        <div ref={chartPngRef}>
            <PngHeader
                metricTitle={metricTitle}
                durations={durations.forWidget}
                webSource={webSource}
                country={country}
            />
            <UtilitiesContainer legendsAmount={series.length}>
                {disclaimer ? <DisclaimerMessage messageKey={disclaimer} /> : null}
                <ButtonsContainer legendsAmount={series.length}>
                    {metric.customGraphSwitcher
                        ? metric.customGraphSwitcher
                        : !showBetaBranchData && (
                              <GraphTypeSwitcher
                                  onItemClick={chartGranularityItemClick}
                                  selectedIndex={chartType === "column" ? 0 : 1}
                                  icons={[
                                      {
                                          name: "bar-chart",
                                          size: "xs",
                                          disabled: isColumnChartTypeDisabled,
                                      },
                                      { name: "line-chart", size: "xs", disabled: false },
                                  ]}
                              />
                          )}
                    <TimeGranularitySwitcher
                        timeGranularity={timeGranularity}
                        granularityUpdate={granularityUpdate}
                        getGranularity={getTimeGranularity}
                    />
                    <DownloadButtonMenu
                        PNG={true}
                        handleDownloadFunction={() =>
                            pngDownload(chartPngRef.current, selectedWidgetTab, downloadPngTrackingEvent)
                        }
                    />
                    {metric.addToDashboardName && !showBetaBranchData ? (
                        <AddToDashboard
                            metric={metric}
                            webSource={webSource}
                            filters={{
                                ShouldGetVerifiedData: showGAApprovedData,
                                timeGranularity: timeGranularity.name,
                                unBounced: meta.isUnBouncedActive,
                            }}
                        />
                    ) : null}
                    <WeShare
                        chartRef={chartPngRef.current}
                        description={metricTitle}
                        title={`${chosenSites[0]} ${metricTitle}`}
                        redirectUrl={`https://www.similarweb.com/website/${chosenSites[0]}/#overview`}
                    />
                </ButtonsContainer>
            </UtilitiesContainer>

            {isSameData ? (
                <BetaMessagewrapper>
                    <BetaMessage messageKey="wa.traffic.engagement.beta.graphs.coalesce" />
                </BetaMessagewrapper>
            ) : null}

            <ChartSection>
                <FlexRow justifyContent="flex-end">
                    {isGraphLoading ? (
                        <GraphLoader
                            width={window.innerWidth > 1440 ? 1280 : 1000}
                            height={288}
                        />
                    ) : hasData ? (
                        <ChartContainer>
                            <Chart
                                type={chartType}
                                data={chartData}
                                config={chartConfig}
                                afterRender={chartAfterRender}
                                chartIdForAnnotations={meta?.isFromAlternativeSources ? null : chartIdForAnnotations} // if fromAlternativeSources is true then we wont show the annotations
                            />
                        </ChartContainer>
                    ) : (
                        <ChartContainer>
                            <NoData
                                paddingTop="80px"
                                noDataTitleKey="global.nodata.notavilable"
                                noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
                            />
                        </ChartContainer>
                    )}

                    {showChart || series.filter((s) => s.isSelected).length < series.length ? (
                        <CheckboxesContainer>
                            {metric.customActionButtons ? metric.customActionButtons : null}
                            {displayCategoryBenchmarkDropdown ? (
                                <CategoryBenchmarkDropdown
                                    isDropdownDisabled={!isCategoryEnabled}
                                    category={category}
                                    text={{
                                        tooltip: translate(categoryBenchmarkDropdownTooltipKey),
                                        searchPlaceHolder: translate(
                                            "traffic_and_engagement.generic_graph.category.dropdown.search_placeholder",
                                        ),
                                    }}
                                    isTooltipVisible={true}
                                    onDropdownToggleCallback={onCategoryDropdownToggle}
                                    onCategorySelectedCallback={onCategorySelected}
                                    placeholder={noCategoryItem}
                                    excludeCustomCategoriesWithSegments={true}
                                />
                            ) : null}
                            <LegendsContainer
                                metric={metric}
                                chartType={chartType}
                                showGAApprovedData={showGAApprovedData}
                                queryParams={{ ...queryParams, beta: showBetaBranchData }}
                                meta={meta}
                                webSource={webSource}
                                data={graph.data}
                                onLegendClick={onLegendClick}
                                sites={series}
                                showCategoryLegend={meta.isCategoryActive}
                            />
                        </CheckboxesContainer>
                    ) : null}
                </FlexRow>
            </ChartSection>
        </div>
    );
};
export const GenericGraph = GenericGraphInner;
